class TuxAPIClient {
	constructor() {
		this.baseUrl =
			window.tuxBaseApiUrl || "https://api.underwearexpert.com";
	}

	// Core method to handle fetch requests
	async fetchJson(endpoint, options = {}) {
		const url = `${this.baseUrl}${endpoint}`;

		const maxRetries = 3;
		let attempt = 0;
		let delay = 3000; // 1 second

		while (attempt < maxRetries) {
			try {
				const response = await fetch(url, {
					method: options.method || "GET",
					headers: {
						"Content-Type": "application/json",
						...(options.headers || {}),
					},
					body: options.body ? JSON.stringify(options.body) : undefined,
				});

				// Do NOT retry on client errors (4xx)
				if (!response.ok) {
					if (response.status >= 400 && response.status < 500) {
						throw new Error(`HTTP ${response.status} (no retry)`);
					}
					throw new Error(`HTTP ${response.status}`);
				}

				return await response.json();
			} catch (error) {
				attempt++;

				// Stop retrying after max attempts
				if (attempt >= maxRetries) {
					console.error(
						`[TuxAPIClient] Fetch failed after ${attempt} attempts`,
						{ url, error: error.message }
					);
					return null;
				}

				console.warn(
					`[TuxAPIClient] Retry ${attempt}/${maxRetries} after ${delay}ms`,
					{ url, error: error.message }
				);

				// wait before retry (exponential backoff)
				await new Promise(resolve => setTimeout(resolve, delay));

				delay *= 2; // exponential
			}
		}
	}

	// Public method to get wizard settings
	async getWizardSettings() {
		return await this.fetchJson("/api/preshipping/wizard_settings/");
	}

	// check prior subscription by temp-id (cart token)
	async getSubscriptionWithTempId(tempId) {
		if (!tempId) return null;
		return await this.fetchJson(
			`/api/preshipping/initial/subscription-with-temp-id/?temp_id=${tempId}`
		);
	}

	async preShippingSetResults(payload) {
		return await this.fetchJson("/api/preshipping/initial/set_result", {
			method: "POST",
			headers: { Accept: "application/json" }, // optional but nice to have
			body: payload || {}, // must be serializable
		});
	}

	getStylesIcons() {
		return {
			briefs: '<svg xmlns="http://www.w3.org/2000/svg" width="75" height="45" viewBox="0 0 75 45" fill="none"><path d="M72.954 1H1V8.35011H72.954V1Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M72.954 8.34964L73 15.6997C60.0648 15.6997 49.2108 24.665 46.0949 36.8255C45.0141 41.0272 41.3347 44 37.069 44C32.8033 44 29.1239 41.0272 28.0431 36.8255C24.9272 24.665 14.0731 15.6997 1.13798 15.6997H1V8.34964" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M27 8L22 26" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M47 8L52 26" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/></svg>',
			trunks: '<svg xmlns="http://www.w3.org/2000/svg" width="76" height="48" viewBox="0 0 76 48" fill="none"><path d="M50.4134 7.85046H25.6956L30.7952 35.0883C31.4436 38.5571 34.499 41.0769 38.049 41.0769C41.6099 41.0769 44.6544 38.5571 45.3028 35.0883L50.4134 7.85046Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M72.4391 1H3.65991V7.85035H72.4391V1Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M32.0373 38.1535L31.6636 47H2L3.65958 7.85046" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M43.9626 38.1535L44.3363 47H74L72.3404 7.85046" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/></svg>',
			jock_straps:
				'<svg xmlns="http://www.w3.org/2000/svg" width="74" height="46" viewBox="0 0 74 46" fill="none"><path d="M72.9998 1H1V8.52092H72.9998V1Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M50.0352 8.52115H24.1484L29.4869 38.4252C30.1657 42.2335 33.3641 45 37.0803 45C40.808 45 43.995 42.2335 44.6738 38.4252L50.0352 8.52115Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M73.0002 8.52115C73.0002 19.1558 57.2726 40.485 39.4395 44.6168" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M65.982 8.52115C64.5784 19.2277 52.1182 39.5868 39.4395 44.6168" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M1 8.52115C1 19.1558 16.7277 40.4252 34.5608 44.5569" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M8.01807 8.52115C9.4217 19.2277 21.9049 39.5389 34.5836 44.5689" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/></svg>',
			thongs: '<svg xmlns="http://www.w3.org/2000/svg" width="74" height="45" viewBox="0 0 74 45" fill="none"><path d="M73 1H1V8.35009H73V1Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/><path d="M50.0352 8.3497H24.1599L29.4983 37.5745C30.1771 41.2964 33.3756 44 37.0918 44C40.8195 44 44.0065 41.2964 44.6853 37.5745L50.0352 8.3497Z" stroke="currentColor" stroke-width="2" stroke-miterlimit="10"/></svg>',
		};
	}

	async getTuxOptions(payload) {
		return await this.fetchJson("/api/preshipping/initial/options", {
			method: "POST",
			headers: { Accept: "application/json" }, // optional but nice to have
			body: payload || {}, // must be serializable
		});
	}
}

/**
 * Sku Class
 * Renders Each Product
 */
class SkuCard {
	constructor(sku) {
		this.sku = sku;
	}

	render() {
		return `
      <div class="refine-popup-card ${this.sku.selected === "yes" ? "selected" : ""
			}" data-id="${this.sku.id}">
	   <input 
          type="checkbox"  
          name="selectedPnrProduct[]" 
          value="${this.sku.id}" 
          ${this.sku.selected === "yes" ? "checked" : ""} 
		  hidden
        >
        <div class="refine-popup-card-in">
          ${this.mediaSection()}
          ${this.contentSection()}
        </div>
      </div>
    `;
	}

	mediaSection() {
		return `
      <div class="refine-popup-card-media">
        ${this.images()}
        ${this.badge()}
        ${this.changeViewButton()}
      </div>
    `;
	}

	images() {
		const imgBack = this.sku?.image_back || "";
		return `
    <img src="${this.sku.image}" alt="${this.sku.title ? this.sku.title : 'refine-image'
			}" class="refine-popup-image">
    ${imgBack
				? `<img src="${imgBack}" alt="${this.sku.title ? this.sku.title : 'refine-pupup-image'} back" class="hover_image">`
				: ""
			}
  `;
	}

	badge() {
		return `
      <div class="badge-circle" style>
        <svg xmlns="http://www.w3.org/2000/svg" width="15" height="10" viewBox="0 0 15 10" aria-hidden="true">
          <path d="M5.80193 7.6341L13.0988 0.336426L14.2221 1.4589L5.80193 9.87905L0.75 4.82712L1.87248 3.70464L5.80193 7.6341Z" fill="white"></path>
        </svg>
      </div>
    `;
	}

	changeViewButton() {
		return `
      <div class="btn-change-view">
        <div class="btn-change-view-inner">
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M3.463 2.43301C5.27756 0.86067 7.59899 -0.00333986 10 9.70266e-06C15.523 9.70266e-06 20 4.47701 20 10C20 12.136 19.33 14.116 18.19 15.74L15 10H18C18.0001 8.43163 17.5392 6.89781 16.6747 5.58927C15.8101 4.28072 14.5799 3.25517 13.1372 2.64013C11.6944 2.0251 10.1027 1.84771 8.55996 2.13003C7.0172 2.41234 5.59145 3.14191 4.46 4.22801L3.463 2.43301ZM16.537 17.567C14.7224 19.1393 12.401 20.0034 10 20C4.477 20 0 15.523 0 10C0 7.86401 0.67 5.88401 1.81 4.26001L5 10H2C1.99987 11.5684 2.46075 13.1022 3.32534 14.4108C4.18992 15.7193 5.42007 16.7449 6.86282 17.3599C8.30557 17.9749 9.89729 18.1523 11.44 17.87C12.9828 17.5877 14.4085 16.8581 15.54 15.772L16.537 17.567Z"
              fill="black"
            ></path>
          </svg>
        </div>
      </div>
    `;
	}

	contentSection() {
		return `
      <div class="refine-popup-content">
        <div class="refine-popup-name">${this.sku.title}</div>
        <div class="refine-popup-desc">${this.sku.color_name}</div>
      </div>
    `;
	}
}

/**
 * Custom Cart Class
 */
class CustomCart {
	constructor(cartJson) {
		this.cartJson = cartJson;
	}

	async processCartData(drawerData) {
		if (!this.cartJson) return null;

		if (window.shouldChangeJs) {
			// // remove old, then add fresh one
			// await this.removeSubscriptionFromCart();
			// // add new product (always happens after remove or if not found)
			// await this.addCartData(drawerData);
			// this.updateCartItems(drawerData.variantId);
			const getCartGraphql = new GetCartGraphql();
			await getCartGraphql.processUpdateCartData(drawerData);
			console.log('here we need update cart')
			return;
		}

		const inCart = this.checkSubscriptionInCart();
		if (!inCart) {
			await this.addCartData(drawerData);
			// this.updateCartItems(drawerData.variantId);
		}

		// refresh cart JSON
		// Currently not required as we are redirecting to checkout
		// await this.getCartData();
	}

	checkSubscriptionInCart() {
		let variants = this.filterSubscriptionInCart();
		console.log(variants);
		return variants.length > 0 ? true : false;
	}

	updateCartItems = () => {
		document.querySelector("page-header").refreshHeader();
		document.querySelector("cart-form").refresh();
	};

	filterSubscriptionInCart() {
		let items = this.cartJson.items?.filter(
			({ handle }) =>
				handle == "club-subscription" ||
				handle == "underwear-club-one-time-box"
		);

		return items;
	}

	async removeSubscriptionFromCart() {
		try {
			const subs = this.filterSubscriptionInCart();

			if (!subs || subs.length === 0) return;

			for (const item of subs) {
				await fetch(window.Shopify.routes.root + "cart/change.js", {
					method: "POST",
					headers: { "Content-Type": "application/json" },
					body: JSON.stringify({
						id: item.key, // Shopify’s unique line item key
						quantity: 0, // set to 0 → removes
					}),
				});
			}
		} catch (e) {
			console.error("[subscription] removeSubscriptionFromCart error", e);
		}
	}

	getUpsellsDataForAddToCart() {
		const idsElement = document.querySelectorAll('.subscription_free_itemscontainer a.related-swatch')
		const ids = Array.from(idsElement).map(el => {
			return el.getAttribute('data-variant-id')
		})

		// {"id": "41034608082995","quantity": 1, // properties: {"_Gifted": "test"}}
		return ids.map(id => {
			return {
				id: id,
				quantity: 1,
				properties: { "_Gifted": "test" }
			}
		});
	}

	async addCartData(drawerData) {
		try {
			// drawerData["properties"] = {
			// 	_temp_id: window.tux.cartToken,
			// 	_ab: window.tux_ab_param || "0",
			// };


			const upsellItems = this.getUpsellsDataForAddToCart();

			const res = await fetch(
				window.Shopify.routes.root + "cart/add.js?_easygift_internal=true",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({ items: [drawerData, ...upsellItems] }),
				}
			);

			// const data = await res.json();
			// console.log('new cart json', data)
			// this.cartJson = data;
			// window.cartJson = data;
		} catch (e) {
			console.error("[subscription] addCartData error", e);
		}
	}

	async changeCartData(drawerData) {
		try {
			// drawerData["properties"] = {
			// 	_temp_id: window.tux.cartToken,
			// 	_ab: window.tux_ab_param || "0",
			// };
			const res = await fetch(
				window.Shopify.routes.root + "cart/change.js",
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
					},
					body: JSON.stringify({
						id: drawerData.id,
						properties: drawerData.properties,
					}),
				}
			);

			const data = await res.json();
			// console.log('new cart json', data)
			// this.cartJson = data;
			// window.cartJson = data;
		} catch (e) {
			console.error("[subscription] changeCartData error", e);
		}
	}

	async getCartData() {
		try {
			const res = await fetch(window.Shopify.routes.root + "cart.js");
			const data = await res.json();
			this.cartJson = data;
			window.cartJson = data;
			return data;
		} catch (e) {
			console.error("[subscription] getCartData error", e);
		}
	}
}


/**
 * Graphql API Client
 */
class GetCartGraphql {

	getCartGraphql() {
		const graphql = this.getQueryCart();
		console.log(graphql, "graphql");
		this.fetchGraphql(graphql).then(response => {
			window.graphqlCart = response?.data.cart
		});
	}

	async fetchGraphql(graphql) {
		const myHeaders = new Headers();
		myHeaders.append("Content-Type", "application/json");
		myHeaders.append("X-Shopify-Storefront-Access-Token", "c055ff923183092afacdba7a7ad09e2b");
		myHeaders.append("Cookie", "request_method=POST");

		const requestOptions = {
			method: "POST",
			headers: myHeaders,
			body: graphql,
			redirect: "follow"
		};

		try {
			const response = await fetch("https://underwearexpert.myshopify.com/api/2024-04/graphql.json", requestOptions);
			const result = await response.json();
			console.log(result, "result")
			return result;
		} catch (error) {
			console.error(error);
		}
	}

	getQueryCart() {
		setTimeout(() => {
			console.log(window.cartJson.token, "token");
		}, 10000);
		return JSON.stringify({
			query: `query MyQuery {
            cart(
                id: "gid://shopify/Cart/${window.cartJson.token}"
            ) {
				checkoutUrl
                lines(first: 50) {
                edges {
                    node {
                    id
                    merchandise {
                        ... on ProductVariant {
                        id
                        product {
                                id
                            }
                        }
                    }
                    }
                }
                }
            }
        }
        `
		});
	}

	getQueryUpdateCartLine(variables) {
		return JSON.stringify({
			query: `mutation MyMutation($cartId: ID!,$lines: [CartLineUpdateInput!]!) {
         cartLinesUpdate(cartId: $cartId, lines: $lines) {
                cart {
                    id
                    lines(first: 10) {
                        nodes {
                         merchandise {
                            ... on ProductVariant {
                            id
                            product {
                                id
                                title
                            }
                            }
                        }
                    }
                }
            }
    }
        }   
                `,
			variables: variables
		});
	}

	getQueryAddCartLine(variables) {
		return JSON.stringify({
			query: `mutation MyMutation($cartId: ID!,$merchandiseId: ID!,$sellingPlanId: ID!, $,attributes:[AttributeInput!]!) {
                cartLinesAdd(cartId: $cartId, lines: {merchandiseId: $merchandiseId, sellingPlanId: $sellingPlanId, attributes: $attributes}) {
                    cart {
                    id
                    lines(first: 10) {
                        nodes {
                        merchandise {
                            ... on ProductVariant {
                            id
                            product {
                                id
                                title
                            }
                            }
                        }
                        }
                    }
                    }
                }
            }
                `,
			variables: variables
		});

	}

	getSubscriptionCartLine() {
		const edge = window.graphqlCart.lines.edges.find(({ node }) => node.merchandise.product.id === `gid://shopify/Product/6804589314099`)
		return edge.node.id
	}

	getUpdateVariables(cartUpdate) {
		const cartId = `gid://shopify/Cart/${window.cartJson.token}`
		const merchandiseId = `gid://shopify/ProductVariant/${cartUpdate.id}`
		const lineId = this.getSubscriptionCartLine();
		const sellingPlanId = `gid://shopify/SellingPlan/${cartUpdate.selling_plan}`
		const variable = {
			cartId: cartId,
			lines: [
				{
					id: lineId,
					merchandiseId: merchandiseId,
					sellingPlanId: sellingPlanId
				}
			]
		}
		return variable;
	}

	updateCartItems() {
		document.documentElement.dispatchEvent(
			new CustomEvent("on:cart:change", {
				bubbles: true,
				cancelable: false,
				detail: {
					openDrawer: false,
					fromMemberPerks: false
				},
			}),
		);

		// Remove upsell 
		$('div[data-merge="cross-sells"]').hide()
	}

	async processUpdateCartData(cartUpdate) {
		try {
			const variable = this.getUpdateVariables(cartUpdate)
			const graphql = this.getQueryUpdateCartLine(variable);
			const response = await this.fetchGraphql(graphql);
			this.updateCartItems();
			console.log(response, "response");
		} catch (error) {
			console.error('Error updating the cart:', error);
		}
	}

}
